// components/review-card.js
class ReviewCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['title', 'content', 'author', 'verified', 'rating', 'product-name', 'product-image', 'loading', 'width', 'height'];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    if (this.shadowRoot) {
      this.render();
    }
  }

  render() {
    const title = this.getAttribute('title') || '';
    const content = this.getAttribute('content') || '';
    const author = this.getAttribute('author') || '';
    const verified = this.getAttribute('verified') !== 'false';
    const rating = parseInt(this.getAttribute('rating') || '5');
    const productName = this.getAttribute('product-name') || '';
    const productImage = this.getAttribute('product-image') || '';
    const lazy = this.getAttribute('loading') === 'lazy';
    const width = this.getAttribute('width') || '64';
    const height = this.getAttribute('height') || '64';

    const stars = this.generateStars(rating);

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: flex;
          flex-shrink: 0;
          min-width: 100%;
          max-width: 439px;
          align-self: stretch;
        }

        @media (min-width: 750px) {
          :host {
            min-width: calc(33.333% - 16px);
          }
        }
        
        .card {
          flex: 1;
          width: 100%;
          position: relative;
          border-radius: 8px;
          background-color: #f8f3ed;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 24px;
          box-sizing: border-box;
          gap: 20px;
          text-align: left;
          font-size: 20px;
          color: #433552;
          font-family: 'Helvetica Neue';
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06);
        }
        
        .frame-parent {
          align-self: stretch;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 20px;
          flex: 1;
        }
        
        .frame-group {
          align-self: stretch;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        }
        
        .star-parent {
          display: flex;
          align-items: center;
        }
        
        .frame-child {
          height: 16px;
          width: 16px;
          position: relative;
          border-radius: 1px;
        }
        
        .frame-item {
          height: 16px;
          width: 16px;
          position: relative;
          border-radius: 1px;
          margin-left: -3px;
        }
        
        .best-face-mask-ever-parent {
          align-self: stretch;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
        }
        
        .best-face-mask {
          align-self: stretch;
          position: relative;
          font-weight: 600;
          font-size: 18px;
          color: #121212;
        }
        
        .im-obsessed-with {
          align-self: stretch;
          position: relative;
          font-size: 16px;
          line-height: 26px;
          color: rgba(18, 18, 18, 0.8);
        }
        
        .lakshmi-g-parent {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #121212;
        }
        
        .lakshmi-g {
          position: relative;
          font-weight: 500;
        }
        
        .vector-parent {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          color: #6b7280;
        }
        
        .vector-icon {
          height: 12px;
          width: 12px;
          position: relative;
          color: #16a34a;
        }
        
        .verified-buyer {
          position: relative;
          line-height: 20px;
          font-weight: 500;
        }
        
        .component-45-inner {
          align-self: stretch;
          background-color: #fff;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 12px;
          font-size: 14px;
          color: #202327;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
        
        .frame-container {
          align-self: stretch;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .frame-div {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0; /* IMPORTANT: Allows text truncation to work */
        }
        
        .frame-icon {
          width: 48px;
          height: 48px;
          border-radius: 5px;
          max-height: 100%;
          object-fit: cover;
          flex-shrink: 0; /* Prevents image from shrinking */
        }
        
        .glow-in-the {
          flex: 1;
          position: relative;
          line-height: 18px;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: normal;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          font-weight: 600;
          word-break: break-word; /* Allows long words to break */
          hyphens: auto; /* Adds hyphenation for long words */
        }

        /* Mobile-specific fixes */
        @media (max-width: 767px) {
          .card {
            padding: 20px 16px;
            gap: 16px;
          }
          
          .frame-container {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          
          .frame-div {
            width: 100%;
          }
          
          .glow-in-the {
            -webkit-line-clamp: 3; /* Show 3 lines on mobile */
            font-size: 13px; /* Slightly smaller font on mobile */
            line-height: 1.4;
          }
          
          .navigation-menu {
            width: 100%;
            justify-content: center;
            padding: 10px 16px;
          }
          
          .frame-icon {
            width: 40px;
            height: 40px;
          }
        }
        
        /* Tablet */
        @media (min-width: 768px) and (max-width: 1023px) {
          .glow-in-the {
            -webkit-line-clamp: 2;
            font-size: 14px;
          }
        }
        
        /* Desktop */
        @media (min-width: 1024px) {
          .glow-in-the {
            -webkit-line-clamp: 2;
            font-size: 14px;
          }
        }

        .component-45-inner {
          margin-top: auto;
        }
        
        .navigation-menu {
          background-color: #f3e4d7;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          gap: 6px;
          color: #2d3748;
          font-family: Inter;
          text-decoration: none;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s ease;
          white-space: nowrap; /* Prevent button text from wrapping */
        }
        
        .navigation-menu:hover {
          background-color: #ead6c6;
        }
        
        .frame-icon2 {
          width: 14px;
          position: relative;
          max-height: 100%;
          display: none;
        }
        
        .search {
          position: relative;
          line-height: 26px;
          font-weight: 500;
        }
        
        /* Accessibility */
        button:focus-visible,
        a:focus-visible {
          outline: 2px solid #6b46c1;
          outline-offset: 2px;
        }
      </style>
      
      <div class="card">
        <div class="frame-parent">
          <div class="frame-group">
            <div class="star-parent">
              ${stars}
            </div>
            <div class="best-face-mask-ever-parent">
              <div class="best-face-mask">${this.escapeHtml(title)}</div>
              <div class="im-obsessed-with">${this.escapeHtml(content)}</div>
            </div>
          </div>
          
          <div class="lakshmi-g-parent">
            <div class="lakshmi-g">${this.escapeHtml(author)}</div>
            ${verified ? `
              <div class="vector-parent">
                <svg class="vector-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <span class="verified-buyer">Verified Buyer</span>
              </div>
            ` : ''}
          </div>
        </div>
        
        ${productName ? `
          <div class="component-45-inner">
            <div class="frame-container">
              <div class="frame-div">
                <img src="${productImage}" 
                     alt="${this.escapeHtml(productName)}" 
                     class="frame-icon"
                     loading="${lazy ? 'lazy' : 'eager'}"
                     width="${width}"
                     height="${height}"
                     onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAiIGhlaWdodD0iNzAiIHZpZXdCb3g9IjAgMCA3MCA3MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNzAiIGhlaWdodD0iNzAiIGZpbGw9IiNGM0Y0RjYiLz48cGF0aCBkPSJNMzUgNDBDMzcuMjA5MSA0MCAzOSAzOC4yMDkxIDM5IDM2QzM5IDMzLjc5MDkgMzcuMjA5MSAzMiAzNSAzMkMzMi43OTA5IDMyIDMxIDMzLjc5MDkgMzEgMzZDMzEgMzguMjA5MSAzMi43OTA5IDQwIDM1IDQwWk0zNSAyMEM0MS42MjcgMjAgNDcgMjUuMzczIDQ3IDMyQzQ3IDM4LjYyNyA0MS42MjcgNDQgMzUgNDRDMjguM373IDQ0IDIzIDM4LjYyNyAyMyAzMkMyMyAyNS4MzczIDI4LjM3MyAyMCAzNSAyMFoiIGZpbGw9IiM5Q0EwQjEiLz48L3N2Zz4='">
                <div class="glow-in-the">${this.escapeHtml(productName)}</div>
              </div>
              <button class="navigation-menu" onclick="window.open('https://oliviasheritage.com/collections/product-bundles', '_blank')">
                <span class="search">Shop Now</span>
              </button>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  generateStars(rating) {
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars += `
          <img class="frame-child" src="https://cdn.shopify.com/s/files/1/0021/8241/4397/files/Star_5.png?v=1769104787" alt="star">
        `;
      } else {
        stars += `
          <img class="frame-item" src="https://cdn.shopify.com/s/files/1/0021/8241/4397/files/Star_5.png?v=1769104787" alt="star" style="opacity: 0.3;">
        `;
      }
    }
    return stars;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  escapeAttr(text) {
    return text.replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
  }
}

// Register the component
if (!customElements.get('review-card')) {
  customElements.define('review-card', ReviewCard);
}